import { err, ok, Result } from "neverthrow"
import { sqlQuery, sqlRun } from "./ldb"
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { selectAllPrettyJson } from "./LdbBrowser"

// creating a migration—
// * peh generateTimestampedToken
// * create a new registration following examples below

// updating a migration—
// * do not update a migration that's gone to prod;
//   follow it up with a new one
// * if you need to update a migration that's been committed to repo
//   but only used by devs prior to production,
//   add code in that deletes versions of objects created before
//   e.g. drop table if exists xyz
//   before creating the updated objects

interface MigrationReceipt {
	id: string
	runAt: number
}

interface Migration {
	id: string
	run: () => Promise<Result<void, FusionError>>
}

export const ldbMigrations = async (): Promise<Result<void, FusionError>> => {
	// 20250712 seems that integer may have been a better affinity choice
	// than datetime, but in practice doesn't seem to matter
	const rCreate = await sqlRun(`
		create table if not exists migration (
			id text primary key,
			runAt datetime not null
		)
	`)
	if(rCreate.isErr()) {
		return err(serializeFusionError('ldbMigrations', rCreate.error))
	}
	const receipts: Record<string, boolean> = {}
	const rSelect = await sqlQuery<MigrationReceipt>(
		`
			select id, runAt
			  from migration
		`,
	)
	if(rSelect.isErr()) {
		return err(serializeFusionError('ldbMigrations', rSelect.error))
	}
	for(let row of rSelect.value) {
		receipts[row.id] = true
	}

	const migrations: Migration[] = []

	migrations.push({
		id: '1uMEj9rrpEaYwAh32k9U3rto1pVPvGR1eK2HPP8I',
		run: async () => {
			// material inventory v2
			// delete earlier works that never made it to production
			let r = await sqlRun(`
				drop table if exists material
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists materialAssignments
			`)
			if(r.isErr()) {
				return(err(r.error))
			}
			// create table materialinventory
			// create table materialClaim
			return ok()
		},
	})

	migrations.push({
		id: '1uzEgJJGPyfQH0LbqY1kWhU2fBKtZAhJ7dxJUaXp',
		run: async () => {
			// 2025 sprint 8 "full offline"
			// update collation and affinity choices
			let r = await sqlRun(`
				drop table if exists materialInventory	
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists materialClaim	
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table materialInventory (
					area text not null collate nocase,
					id text not null collate nocase,
					type text not null,
					status text not null,
					-- see code for /api/v2/offline/table/material-inventory for
					-- more information on the key design for this table
					primary key (area, id)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table materialClaim (
					area text not null collate nocase,
					id text not null collate nocase,
					encounterGuid text not null,
					claimedAt integer not null,
					primary key (area, id)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// fill in the older base set of tables
			// that had been created by ldbInit function before 2025 S07
			r = await sqlRun(`
				drop table if exists draft	
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists drugName	
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists drugSearchWord
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table drugSearchWord (
					word text primary key collate nocase
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists icdCode
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists icd
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table icd (
					Icd10Code text not null primary key,
					Description text not null,
					HistoryOf text
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists product
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists drugProduct
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				CREATE TABLE IF NOT EXISTS drugProduct (
					ID INTEGER PRIMARY KEY,
					Name TEXT
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists productMap
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists drugProductMap
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				CREATE TABLE IF NOT EXISTS drugProductMap (
					ProductID INTEGER NOT NULL,
					Icd10Code TEXT NOT NULL,
					UNIQUE (ProductID, Icd10Code)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// new sprint 8 setting table
			r = await sqlRun(`
				drop table if exists setting
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table setting (
					id text not null primary key,
					value text
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// full offline
			r = await sqlRun(`
				drop table if exists cloudToDeviceBlob
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table cloudToDeviceBlob (
					type text not null,
					id text not null,
					upn text not null,
					data blob not null,
					encoding text not null,
					etag text,
					contentType text,
					syncedAt integer not null,
					lastAccessedAt integer not null,
					primary key (type, id, upn)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table if exists apptEncounter
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table apptEncounter (
					serviceApptId text not null,
					upn text not null,
					encounterGuid text,
					error text,
					primary key (serviceApptId, upn)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// r = await sqlRun(`
			// 	create table branchFileConcept (
			// 		branchGuid text not null,
			// 		cid text not null,
			// 		primary key (branchGuid, cid)
			// 	)
			// `)
			// if(r.isErr()) {
			// 	return err(r.error)
			// }
			r = await sqlRun(`
				drop table if exists deviceToCloudBlob
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table deviceToCloudBlob (
					ordinal integer not null primary key autoincrement,
					type text not null,
					id text not null,
					upn text not null,
					data blob not null,
					encoding text not null,
					nextCodeNext text,
					contentType text,
					createdAt integer not null,
					syncedAt integer
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			return ok()
		},
	})

	migrations.push({
		id: '1viAg6P5nFzLvnCPZ2xalH68xS32fH0KFEFjO8o1',
		run: async () => {
			// 20260112 add ComorbidityStatus column to icd table
			let r = await sqlRun(`
				drop table if exists icd
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table icd (
					Icd10Code text not null primary key,
					Description text not null,
					HistoryOf text,
					ComorbidityStatus text
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			return ok()
		},
	})

	migrations.push({
		id: '1vlXZCyzuD3z5eHMqiglCCMnrM5lSUVhH80Dd1pl',
		run: async () => {
			// 20260129 add new table for dashboardNotifications
			let r = await sqlRun(`
				drop table if exists dashboardNotifications
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table dashboardNotification (
					Id integer primary key autoincrement,
					IsPersonal integer not null,
					Notification text not null,
					EffectiveOn text not null,
					ExpiresOn text not null
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			return ok()
		},
	})

	migrations.push({
		id: '1wD5PlASGOEvy41cMl9InwFER3I8qvDQRsfkhTX1',
		run: async () => {
			// 20260415 replace `id` in deviceToCloudBlob with
			// more specific `encounterGuid` and `fileId` columns
			// this fixes an issue uploading multiple files
			// for a single encounter guid
			let r = await sqlRun(`
				alter table deviceToCloudBlob rename to deviceToCloudBlobOld
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				create table deviceToCloudBlob (
					ordinal integer not null primary key autoincrement,
					type text not null,
					encounterGuid text not null,
					fileId text,
					upn text not null,
					data blob not null,
					encoding text not null,
					nextCodeNext text,
					contentType text,
					createdAt integer not null,
					syncedAt integer
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				insert into deviceToCloudBlob
					(ordinal, type, encounterGuid, fileId, upn, data, encoding,
					nextCodeNext, contentType, createdAt, syncedAt)
				select
					ordinal,
					type,
					case when type = 'encounterFile' then 'unknown' else id end,
					case when type = 'encounterFile' then id else null end,
					upn, data, encoding, nextCodeNext, contentType, createdAt, syncedAt
				from deviceToCloudBlobOld
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			r = await sqlRun(`
				drop table deviceToCloudBlobOld
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			return ok()
		},
	})

	migrations.push({
		id: '1wpaS068daPM2aQfk9fV2Yb4q0h7y5YPcBfXZp8V',
		run: async () => {
			let r = await sqlRun(`
				alter table icd rename to drugIcd
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// re-cache on next sync with new id 'drug-icd'
			r = await sqlRun(`
				delete from cloudToDeviceBlob
				 where type = 'offlineTable'
				   and id = 'icd'
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			// order of primary key columns below allows to quickly select
			// all for a specific date of service, OR
			// all for a specific date of service where Icd10Code like 'XX%'
			r = await sqlRun(`
				create table unfilteredIcd (
					DateOfService text not null,
					Icd10Code text not null,
					Description text not null,
					primary key (DateOfService, Icd10Code)
				)
			`)
			if(r.isErr()) {
				return err(r.error)
			}
			return ok()
		},
	})

	for(let m of migrations) {
		if(receipts[m.id]) {
			// migration already run
			continue
		}
		let r = await m.run()
		if(r.isErr()) {
			return err(serializeFusionError(`migration ${m.id}`, r.error))
		}
		r = await sqlRun(
			`
				insert into migration
				values (?, unixepoch())	
			`,
			[
				m.id,
			],
		)
		if(r.isErr()) {
			return err(serializeFusionError('register', r.error))
		}
	}

	return ok()
}

export const selectAllMigrationJson = async (): Promise<string> => {
	return selectAllPrettyJson('migration', ['id', 'runAt'])
}
